import type { SectorDefinition, SeriesValue } from '../types';

export interface SegmentSelection {
  /** Sub-sector (stage) option index. 0 = All. */
  stage: number;
  /** Period (year) option index. 0 = current. */
  year: number;
}

export const NO_FILTER: SegmentSelection = { stage: 0, year: 0 };

export function isBaseline(sel: SegmentSelection): boolean {
  return sel.stage === 0 && sel.year === 0;
}

/**
 * Returns a sector view with the selected period/sub-sector overrides applied.
 * Stage overrides take precedence over year overrides over the baseline.
 * Returns the original sector unchanged when no filter is active or no
 * segment data exists.
 */
export function applySegments(
  sector: SectorDefinition,
  sel: SegmentSelection,
): SectorDefinition {
  if (isBaseline(sel) || !sector.segments) return sector;

  const yOv = sel.year > 0 ? sector.segments.years?.[String(sel.year)] : undefined;
  const sOv = sel.stage > 0 ? sector.segments.stages?.[String(sel.stage)] : undefined;
  if (!yOv && !sOv) return sector;

  const kpiValue = (id: string): string | undefined =>
    sOv?.kpis?.[id]?.value ?? yOv?.kpis?.[id]?.value;
  const regionIndex = (id: string): number | undefined =>
    sOv?.regions?.[id] ?? yOv?.regions?.[id];

  // merged series
  const series: Record<string, SeriesValue> = { ...sector.series };
  for (const key of Object.keys(series)) {
    const v = sOv?.series?.[key] ?? yOv?.series?.[key];
    if (v) series[key] = v;
  }

  const cloneContent = (loc: 'ar' | 'en') => {
    const c = sector.content[loc];
    return {
      ...c,
      kpis: c.kpis.map((k) => {
        const v = kpiValue(k.id);
        return v ? { ...k, value: v } : k;
      }),
      regions: {
        ...c.regions,
        rows: c.regions.rows.map((r) => {
          const idx = regionIndex(r.id);
          return idx !== undefined ? { ...r, index: idx } : r;
        }),
      },
    };
  };

  return {
    ...sector,
    series,
    content: { ar: cloneContent('ar'), en: cloneContent('en') },
  };
}
